import { Link } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import { EmptyState } from '../src/components/ui/empty-state';
import { Button } from '../src/components/ui/button';
import { PageContainer } from '../src/components/ui/page-container';
import { DocumentTitle } from '../components/DocumentTitle';
import { DEFAULT_STATE_HUB_PATH, DEFAULT_STATE_DISPLAY, buildStateDirectoryRootUrl } from '../lib/stateRoutes';
import { buildStateHubUrl, stateSlugToDisplay } from '../lib/stateCore.mjs';
import { useRouteStateSlugOrNull } from '../hooks/useRouteStateSlug';

/**
 * Catch-all route (audit L7).
 *
 * WHAT THIS FIXES. Three of the four dead-URL classes already had a sensible state
 * before this existed — a removed gym renders "Gym not found" with a Back to
 * directory action, a missing post renders "Post not found", and an unknown suburb
 * renders the directory's empty state. The fourth had nothing: any address outside
 * the /nsw, /vic, /gyms and /blog namespaces fell through `** → /index.html` and
 * rendered the COMPLETE HOMEPAGE at HTTP 200, with no robots marker and the
 * homepage's own canonical — so a visitor never learned their link was wrong and
 * Google filed the URL as a duplicate of the homepage ("Alternative page with
 * proper canonical tag"). Verified live on /this-page-does-not-exist and
 * /styles/boxing before the fix.
 *
 * The server half is `** → /notfound.html` in firebase.json, which supplies the raw
 * `noindex,follow` without needing JS. This component is the human half.
 *
 * Deliberately NOT applied to the gym/blog/suburb states: theirs are better than a
 * generic page because they offer the right next step for where the visitor was
 * heading. This is only for "we have no idea what you were looking for".
 *
 * ⭐ STATE-AWARE ONWARD LINKS (operator list item 9, option B, ruled 2026-08-17).
 * A dead address still tells us something: `/vic/some-nonsense` failed inside VICTORIA.
 * This page used to answer every such visitor with "Browse NSW suburbs" — the same
 * defect class the operator ruled on twice today for the directory headings (items 6/7),
 * appearing here because the links were pinned to DEFAULT_STATE rather than derived.
 *
 * ⚠️ Uses `useRouteStateSlugOrNull`, NOT `useRouteStateSlug`: the defaulting variant
 * answers NSW for a state-less dead URL like `/some-nonsense`, which would send a
 * visitor who never mentioned a state into NSW as though they had. Null is the honest
 * answer there and the generic wording is correct for it.
 *
 * ⭐ THE HTTP STATUS — CORRECTED 2026-08-28 (UI-141 ride-along). This block used to say
 * that dead URLs still return 200 with `noindex`, and that the status-code work was
 * blocked behind prerendering another route. **BOTH WERE TRUE WHEN WRITTEN AND ARE NOW
 * FALSE.** Dead URLs return a TRUE HTTP 404: the `** → /notfound.html` catch-all rewrite
 * is gone from `firebase.json` (only `/photo` and `/photo-meta` rewrites remain), and
 * `scripts/generate-notfound.mjs` also emits `dist/404.html` — the name Firebase Hosting
 * actually serves, and serves with a 404 status. Built by D&P v9 and shipped in deploy
 * #30; `docs/state-expansion/vic/item9-404-options-2026-08-17.md` carries the same
 * correction in its own §5.
 *
 * ⚠️ Re-measured live rather than inherited: `/this-page-does-not-exist` → **404** and a
 * dead gym url → **404**, both `noindex,follow`; CONTROL `/nsw/gyms` → **200**. The
 * superseded claim is quoted above rather than deleted, per the tombstone convention
 * (protocol §3.4(g)) — a confident, detailed note that has gone false is worse than no
 * note, because it is exactly the kind a reader trusts instead of checking.
 */
export default function NotFoundPage() {
    // The state the FAILED url was in, if any — see the note above on why this is the
    // nullable derivation rather than the defaulting one.
    const deadRouteState = useRouteStateSlugOrNull();
    const hubPath = deadRouteState ? buildStateHubUrl(deadRouteState) : DEFAULT_STATE_HUB_PATH;
    const hubLabel = deadRouteState ? stateSlugToDisplay(deadRouteState) : DEFAULT_STATE_DISPLAY;
    // A known state gets that state's directory; otherwise the chooser, whose whole job
    // is that choice. ⚠️ The old link passed `triggerFilters` to `/gyms`, where it is
    // INERT — /gyms is the chooser, not overlay space (DirectoryPage :330). Dropped
    // rather than carried forward as decoration.
    const findGymPath = deadRouteState ? buildStateDirectoryRootUrl(deadRouteState) : '/gyms';

    return (
        <>
            <DocumentTitle title="Page not found – Martial Arts Network" />
            <div className="h-header flex-shrink-0" />
            {/* UI-50: main landmark. This page had none, so the skip link had no
                target and screen-reader landmark navigation had nothing to land on. */}
            <main id="main-content">
            <PageContainer>
                <EmptyState
                    headingLevel={1}
                    icon={SearchX}
                    title="Page not found"
                    description="We couldn't find that page. It may have moved, or the link may be out of date."
                    action={
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <Button asChild>
                                <Link to={hubPath}>Browse {hubLabel} suburbs</Link>
                            </Button>
                            <Button variant="secondary" asChild>
                                <Link to={findGymPath}>Find a gym</Link>
                            </Button>
                            <Button variant="secondary" asChild>
                                <Link to="/blog">Read the blog</Link>
                            </Button>
                        </div>
                    }
                />
            </PageContainer>
            </main>
        </>
    );
}
